import { useState, useRef, useEffect } from "react";
import { useSelectedPersona } from "@/hooks/use-selected-persona";
import { usePersonasQuery } from "@/api/personas";
import {
  useUploadAndTrainLoraMutation,
  usePersonaLorasQuery,
  useDeleteLoraMutation,
} from "@/api/loras";
import { useToast } from "@/hooks/use-toast";
import { useReviewContentQuery } from "@/api/content-review";
import { apiUrl } from "@/api/api";

import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
  CardFooter,
} from "@/components/ui/card";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  X,
  Upload,
  FileImage,
  Sparkles,
  Check,
  Loader2,
  Image as ImageIcon,
  FolderUp,
  Database,
  Trash2,
  Eye,
  ArrowLeft,
} from "lucide-react";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useTabUrlState } from "@/hooks/use-tab-url-state";

// Form validation schema
const formSchema = z.object({
  name: z.string().min(3, "Name must be at least 3 characters"),
  trigger_word: z.string().min(3, "Trigger word must be at least 3 characters"),
  files: z.array(z.instanceof(File)).min(0, "At least 8 images are required"),
});

type FormValues = z.infer<typeof formSchema>;

// Props for return callback
type LoraTrainingPageProps = {
  onReturn: () => void;
};

const LoraTrainingPage: React.FC<LoraTrainingPageProps> = ({ onReturn }) => {
  const [selectedPersonaId] = useSelectedPersona();
  const { data: personas = [] } = usePersonasQuery();
  const [previewImages, setPreviewImages] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [imageSource, setImageSource] = useState<"upload" | "existing">(
    "upload",
  );
  const [selectedExistingImages, setSelectedExistingImages] = useState<
    string[]
  >([]);

  const tabs = ["newmodel", "custommodels"];
  const [activeTab, setActiveTab] = useTabUrlState(tabs, "");

  // Check if we should start in table view mode (from My Models button)

  const [previewImages2, setPreviewImages2] = useState<{
    loraId: string;
    images: string[];
  }>({ loraId: "", images: [] });
  const [showImagePreview, setShowImagePreview] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  // Get previous generated images for selection
  const { data: previousContent } = useReviewContentQuery({
    persona: selectedPersonaId,
    status: "image_generated",
    order_by: "timestamp",
  });

  // Get all existing loras for the selected persona
  const { data: personaLoras = [], isLoading: isLoadingLoras } =
    usePersonaLorasQuery(selectedPersonaId);

  // Delete lora mutation
  const { mutate: deleteLora } = useDeleteLoraMutation();

  const selectedPersona = personas.find(
    (persona) => persona.persona_id === selectedPersonaId,
  );

  const [imageSubject, setImageSubject] = useState<"persona" | "custom">(
    "persona",
  );

  // Form definition
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      trigger_word: selectedPersona?.name || "",
      files: [],
    },
  });

  // Update trigger word when persona changes or image subject changes
  useEffect(() => {
    if (imageSubject === "persona" && selectedPersona) {
      form.setValue("trigger_word", selectedPersona.name);
    }
    if (imageSubject === "custom") {
      form.setValue("trigger_word", "");
    }
  }, [selectedPersona, imageSubject, form]);

  // Mutation for training
  const { mutate: trainLora, isPending } = useUploadAndTrainLoraMutation();

  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);

    // Validate file types (images only)
    const validFiles = selectedFiles.filter((file) =>
      file.type.startsWith("image/"),
    );

    if (validFiles.length !== selectedFiles.length) {
      toast({
        description: "Only image files are allowed",
        variant: "destructive",
      });
    }

    // Update form values
    const currentFiles = form.getValues("files");
    const newFiles = [...currentFiles, ...validFiles];
    form.setValue("files", newFiles, { shouldValidate: true });

    // Update preview images
    const newPreviews = validFiles.map((file) => URL.createObjectURL(file));
    setPreviewImages((prev) => [...prev, ...newPreviews]);
  };

  // Remove a file
  const removeFile = (index: number) => {
    const currentFiles = form.getValues("files");
    const newFiles = currentFiles.filter((_, i) => i !== index);
    form.setValue("files", newFiles, { shouldValidate: true });

    // Also remove preview
    setPreviewImages((prev) => prev.filter((_, i) => i !== index));
  };

  // Toggle existing image selection
  const toggleExistingImage = (imageUrl: string) => {
    console.log("Toggling image URL:", imageUrl);
    setSelectedExistingImages((prev) => {
      // Check if this URL is already selected
      const isCurrentlySelected = prev.includes(imageUrl);

      if (isCurrentlySelected) {
        // If selected, remove it
        const newSelection = prev.filter((url) => url !== imageUrl);
        console.log("Removed from selection. New count:", newSelection.length);
        return newSelection;
      } else {
        // If not selected, add it
        const newSelection = [...prev, imageUrl];
        console.log("Added to selection. New count:", newSelection.length);
        return newSelection;
      }
    });
  };

  // View training images for a LoRA
  const viewTrainingImages = (loraId: string, images: string[]) => {
    // Filter out any empty image URLs
    const validImages = images.filter((img) => img && img.trim() !== "");

    setPreviewImages2({
      loraId,
      images: validImages,
    });
    setShowImagePreview(true);
  };

  // Handle deleting a LoRA
  const handleDeleteLora = (loraId: string) => {
    if (
      window.confirm(
        "Are you sure you want to delete this model? This action cannot be undone.",
      )
    ) {
      deleteLora(
        {
          personaId: selectedPersonaId,
          loraId,
        },
        {
          onSuccess: () => {
            toast({
              description: "Model deleted successfully",
            });
          },
          onError: (error) => {
            console.error("Error deleting model:", error);
            toast({
              description: "Failed to delete model",
              variant: "destructive",
            });
          },
        },
      );
    }
  };

  // Get status badge styling based on status
  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return (
          <Badge
            variant="outline"
            className="bg-yellow-500/20 text-yellow-400 border-yellow-500"
          >
            Pending
          </Badge>
        );
      case "training":
        return (
          <Badge
            variant="outline"
            className="bg-blue-500/20 text-blue-400 border-blue-500"
          >
            Training
          </Badge>
        );
      case "completed":
        return (
          <Badge
            variant="outline"
            className="bg-green-500/20 text-green-400 border-green-500"
          >
            Completed
          </Badge>
        );
      case "failed":
        return (
          <Badge
            variant="outline"
            className="bg-red-500/20 text-red-400 border-red-500"
          >
            Failed
          </Badge>
        );
      default:
        return (
          <Badge
            variant="outline"
            className="bg-gray-500/20 text-gray-400 border-gray-500"
          >
            Unknown
          </Badge>
        );
    }
  };

  // Format date for better display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Helper to convert image URLs to Files
  const urlsToFiles = async (urls: string[]): Promise<File[]> => {
    const files: File[] = [];

    try {
      await Promise.all(
        urls.map(async (url, index) => {
          const fileName = `image_${index + 1}.jpg`;

          try {
            // Fetch the image
            const response = await fetch(
              `${apiUrl}/get-file?url=${encodeURIComponent(url)}`,
            );
            const blob = await response.blob();

            // Create a File from the blob
            const file = new File([blob], fileName, { type: blob.type });
            files.push(file);
          } catch (err) {
            console.error(`Failed to convert URL to file: ${url}`, err);
          }
        }),
      );
    } catch (err) {
      console.error("Error in urlsToFiles:", err);
    }

    return files;
  };

  // Submit form
  const onSubmit = async (data: FormValues) => {
    if (!selectedPersonaId) {
      toast({
        description: "No agent selected",
        variant: "destructive",
      });
      return;
    }

    // Check if we have enough images based on the selected source
    if (imageSource === "upload" && data.files.length < 8) {
      toast({
        description: "At least 8 uploaded images are required",
        variant: "destructive",
      });
      return;
    }

    if (imageSource === "existing" && selectedExistingImages.length < 8) {
      toast({
        description: "At least 8 existing images must be selected",
        variant: "destructive",
      });
      return;
    }

    // Prevent multiple submissions
    if (isUploading || isPending) {
      return;
    }

    setIsUploading(true);

    // Fixed training steps to 1000
    const trainingSteps = 1000;

    try {
      let filesToSubmit = data.files;

      // If using existing images, convert them to Files
      if (imageSource === "existing") {
        toast({
          description: "Processing selected images...",
        });

        filesToSubmit = await urlsToFiles(selectedExistingImages);

        if (filesToSubmit.length < 8) {
          setIsUploading(false);
          toast({
            description: `Error: Could only process ${filesToSubmit.length} images out of ${selectedExistingImages.length}`,
            variant: "destructive",
          });
          return;
        }
      }

      // Create payload - don't set files if using existing images with empty upload files
      const payload = {
        name: data.name,
        persona_id: selectedPersonaId,
        trigger_word: data.trigger_word,
        training_steps: trainingSteps,
        image_subject: imageSubject,
        files: filesToSubmit,
      };

      // Single API call to create a training job
      trainLora(payload, {
        onSuccess: (response) => {
          setIsUploading(false);
          console.debug(response);
          toast({
            description: "Training started successfully!",
          });
          // Clear form data to prevent accidental resubmission
          if (imageSource === "upload") {
            form.setValue("files", []);
            setPreviewImages([]);
          } else {
            setSelectedExistingImages([]);
          }
          // Switch to table view to show the status
          setActiveTab("custommodels");
        },
        onError: (error) => {
          console.error("Error starting training:", error);
          setIsUploading(false);
          toast({
            description: "Failed to start training",
            variant: "destructive",
          });
        },
      });
    } catch (error) {
      console.error("Error preparing images for training:", error);
      setIsUploading(false);
      toast({
        description: "Failed to start training",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="container px-8 py-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-primary">
            Custom Model Training
          </h1>
          <p className="text-white/60">
            {activeTab === "newmodel"
              ? "Train a custom model based on your images"
              : "Manage custom models"}
          </p>
        </div>
        <div className="flex gap-4">
          {activeTab === "newmodel" && (
            <Button
              variant="outline"
              onClick={() => {
                setActiveTab("custommodels");
              }}
              className="border border-white/10 hover:bg-[#DFFE00]/10 hover:border-primary/50 text-primary transition-all"
            >
              <Database className="mr-2 h-4 w-4" />
              {selectedPersona?.name}'s models
            </Button>
          )}
          {activeTab === "custommodels" && (
            <Button
              variant="outline"
              onClick={() => {
                setActiveTab("newmodel");
              }}
              className="border border-white/10 hover:bg-[#DFFE00]/10 hover:border-primary/50 text-primary transition-all"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Training
            </Button>
          )}
          <Button
            variant="ghost"
            onClick={onReturn}
            className="border border-white/10 hover:bg-white/5"
          >
            Back to Image Generation
          </Button>
        </div>
      </div>

      {activeTab === "newmodel" ? (
        // Training Form View
        <div className="grid md:grid-cols-2 gap-6">
          {/* Training Form */}
          <Card className="bg-black/20 border-white/10 backdrop-blur-sm hover:border-primary/20 transition-all duration-200">
            <CardHeader className="px-6 pt-6">
              <CardTitle className="text-xl font-semibold text-primary">
                Training Settings
              </CardTitle>
              <CardDescription className="text-white/60">
                Configure your custom model training
              </CardDescription>
            </CardHeader>
            <CardContent className="px-6">
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-6"
                >
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-white">Model Name</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="E.g., My Custom Style"
                            className="bg-black/40 border-white/10 text-white"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="space-y-2 mb-4">
                    <FormLabel className="text-white">Image Subject</FormLabel>
                    <Select
                      value={imageSubject}
                      onValueChange={(value) =>
                        setImageSubject(value as "persona" | "custom")
                      }
                    >
                      <SelectTrigger className="bg-black/40 border-white/10 hover:border-primary/50 text-white py-2 px-3">
                        <SelectValue placeholder="Select image subject">
                          {imageSubject === "persona"
                            ? "My agent"
                            : "Custom Subject"}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent className="bg-black/90 border-white/10 backdrop-blur-md">
                        <SelectItem
                          value="persona"
                          className="text-white hover:bg-[#DFFE00]/10 transition-colors"
                        >
                          My agent
                        </SelectItem>
                        <SelectItem
                          value="custom"
                          className="text-white hover:bg-[#DFFE00]/10 transition-colors"
                        >
                          Custom Subject
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <FormField
                    control={form.control}
                    name="trigger_word"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-white">
                          Trigger Word
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Word to activate this style"
                            className="bg-black/40 border-white/10 text-white"
                            disabled={imageSubject === "persona"}
                            {...field}
                          />
                        </FormControl>
                        {imageSubject === "persona" ? (
                          <p className="text-xs text-white/60 mt-1">
                            For agent models, the trigger word is fixed to the
                            agent name
                          </p>
                        ) : (
                          <p className="text-xs text-white/60 mt-1">
                            The trigger word will activate this style in your
                            prompts
                          </p>
                        )}
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="files"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-white">
                          Training Images (
                          {imageSource === "upload"
                            ? field.value.length
                            : selectedExistingImages.length}
                          /8)
                        </FormLabel>
                        <FormControl>
                          <div className="space-y-4">
                            {/* Image Source Tabs */}
                            <Tabs
                              defaultValue="upload"
                              value={imageSource}
                              onValueChange={(val) =>
                                setImageSource(val as "upload" | "existing")
                              }
                            >
                              <TabsList className="bg-black/30 border border-white/10">
                                <TabsTrigger
                                  value="upload"
                                  className="data-[state=active]:bg-[#DFFE00]/10 data-[state=active]:text-primary"
                                >
                                  <FolderUp className="mr-2 h-4 w-4" />
                                  Upload Files
                                </TabsTrigger>
                                <TabsTrigger
                                  value="existing"
                                  className="data-[state=active]:bg-[#DFFE00]/10 data-[state=active]:text-primary"
                                >
                                  <ImageIcon className="mr-2 h-4 w-4" />
                                  Existing Images
                                </TabsTrigger>
                              </TabsList>

                              <TabsContent value="upload">
                                <div
                                  className="border-2 border-dashed border-white/20 rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-all"
                                  onClick={() => fileInputRef.current?.click()}
                                >
                                  <Upload className="h-12 w-12 mx-auto text-white opacity-40 mb-2" />
                                  <p className="text-white/70">
                                    Click to upload images
                                  </p>
                                  <p className="text-white/50 text-sm mt-1">
                                    Upload at least 8 similar images
                                  </p>
                                  <input
                                    type="file"
                                    multiple
                                    accept="image/*"
                                    className="hidden"
                                    ref={fileInputRef}
                                    onChange={handleFileChange}
                                  />
                                </div>
                              </TabsContent>

                              <TabsContent value="existing">
                                <div className="border border-white/20 rounded-lg p-4">
                                  {(previousContent?.pages?.[0]?.data?.length ??
                                    0) > 0 ? (
                                    <div className="grid grid-cols-3 gap-2 max-h-[350px] overflow-y-auto pr-2">
                                      {(
                                        previousContent?.pages[0].data ?? []
                                      ).map((item) => {
                                        const imageUrl =
                                          item.optimized_url ||
                                          item.thumbnail_url;
                                        const isSelected =
                                          selectedExistingImages.includes(
                                            imageUrl,
                                          );

                                        return (
                                          <div
                                            key={item.doc_id}
                                            className={`relative aspect-square  cursor-pointer ${
                                              isSelected
                                                ? "border-2 border-[#DFFE00]"
                                                : "border border-white/10"
                                            }`}
                                            onClick={() =>
                                              toggleExistingImage(imageUrl)
                                            }
                                          >
                                            <img
                                              src={`${apiUrl}/get-file?url=${encodeURIComponent(imageUrl)}`}
                                              alt="Existing image"
                                              className="w-full h-full object-cover"
                                            />
                                            {isSelected && (
                                              <div className="absolute top-2 right-2">
                                                <div className="bg-[#DFFE00] rounded-full p-1">
                                                  <Check className="h-4 w-4 text-black" />
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  ) : (
                                    <div className="flex flex-col items-center justify-center py-8 text-white/40">
                                      <ImageIcon className="h-12 w-12 mb-2" />
                                      <p>No existing images available</p>
                                    </div>
                                  )}
                                </div>
                                <p className="text-xs text-white/60 mt-2">
                                  Select at least 8 images to use for training
                                </p>
                                {imageSource === "existing" &&
                                  selectedExistingImages.length < 8 && (
                                    <p className="text-red-500 text-xs mt-1">
                                      At least 8 images are required
                                    </p>
                                  )}
                              </TabsContent>
                            </Tabs>

                            {imageSource === "upload" &&
                              field.value.length < 8 && (
                                <p className="text-red-500 text-xs mt-2">
                                  At least 8 images are required
                                </p>
                              )}
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button
                    type="submit"
                    className="w-full h-12 bg-[#DFFE00] hover:bg-[#DFFE00]/90 text-black font-bold transition-all duration-200"
                    disabled={
                      isUploading ||
                      isPending ||
                      (imageSource === "upload" &&
                        form.getValues("files").length < 8) ||
                      (imageSource === "existing" &&
                        selectedExistingImages.length < 8)
                    }
                  >
                    {isUploading || isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        STARTING TRAINING...
                      </>
                    ) : (
                      <>
                        <Sparkles className="mr-2 h-5 w-5" />
                        START TRAINING
                      </>
                    )}
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>

          {/* Image Preview */}
          <Card className="bg-black/20 border-white/10 backdrop-blur-sm hover:border-primary/20 transition-all duration-200">
            <CardHeader className="px-6 pt-6">
              <CardTitle className="text-xl font-semibold text-primary">
                Training Images
              </CardTitle>
              <CardDescription className="text-white/60">
                Preview of images being used for training
              </CardDescription>
            </CardHeader>
            <CardContent className="px-6">
              <div className="grid grid-cols-3 gap-2 max-h-[400px] overflow-y-auto p-1">
                {imageSource === "upload" ? (
                  // Show uploaded images
                  previewImages.length > 0 ? (
                    previewImages.map((preview, index) => (
                      <div
                        key={index}
                        className="relative group aspect-square border border-white/10 rounded-md overflow-hidden"
                      >
                        <img
                          src={preview}
                          alt={`Preview ${index}`}
                          className="w-full h-full object-cover"
                        />
                        <button
                          className="absolute top-1 right-1 bg-black/70 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => removeFile(index)}
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))
                  ) : (
                    <div className="col-span-3 flex flex-col items-center justify-center h-64 border border-white/10 rounded-lg">
                      <FileImage className="h-16 w-16 text-white opacity-20 mb-2" />
                      <p className="text-white/40">No images selected</p>
                    </div>
                  )
                ) : // Show selected existing images
                selectedExistingImages.length > 0 ? (
                  selectedExistingImages.map((imageUrl, index) => (
                    <div
                      key={index}
                      className="relative group aspect-square border border-white/10 rounded-md overflow-hidden"
                    >
                      <img
                        src={`${apiUrl}/get-file?url=${encodeURIComponent(imageUrl)}`}
                        alt={`Selected ${index}`}
                        className="w-full h-full object-cover"
                      />
                      <button
                        className="absolute top-1 right-1 bg-black/70 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => toggleExistingImage(imageUrl)}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="col-span-3 flex flex-col items-center justify-center h-64 border border-white/10 rounded-lg">
                    <FileImage className="h-16 w-16 text-white opacity-20 mb-2" />
                    <p className="text-white/40">No existing images selected</p>
                  </div>
                )}
              </div>
            </CardContent>
            <CardFooter className="px-6 py-4 border-t border-white/10">
              <div className="text-white/60 text-sm">
                <p>
                  Upload similar images for best results. Consistent lighting,
                  angles, and subjects will produce better models.
                </p>
              </div>
            </CardFooter>
          </Card>
        </div>
      ) : (
        // My Models View (Table)
        <Card className="bg-black/20 border-white/10 backdrop-blur-sm hover:border-primary/20 transition-all duration-200">
          <CardHeader className="px-6 pt-6">
            <CardTitle className="text-xl font-semibold text-primary">
              {selectedPersona?.name}'s Models
            </CardTitle>
            <CardDescription className="text-white/60">
              View and manage {selectedPersona?.name}'s' training models
            </CardDescription>
          </CardHeader>
          <CardContent className="px-6">
            {isLoadingLoras ? (
              <div className="flex justify-center items-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : personaLoras.length === 0 ? (
              <div className="text-center py-12 border border-white/10 rounded-lg">
                <Database className="h-12 w-12 mx-auto text-white opacity-20 mb-4" />
                <p className="text-white/60">No custom models found</p>
                <Button
                  className="mt-4 bg-[#DFFE00]/10 hover:bg-[#DFFE00]/20 text-primary"
                  onClick={() => setActiveTab("newmodel")}
                >
                  Create Your First Model
                </Button>
              </div>
            ) : (
              <div className="rounded-md border border-white/10 overflow-hidden">
                <Table>
                  <TableHeader className="bg-black/40">
                    <TableRow className="hover:bg-black/30 border-white/10">
                      <TableHead className="text-white">Name</TableHead>
                      <TableHead className="text-white">Trigger Word</TableHead>
                      <TableHead className="text-white">Status</TableHead>
                      <TableHead className="text-white">Created</TableHead>
                      <TableHead className="text-white">Updated</TableHead>
                      <TableHead className="text-white">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {personaLoras.map((lora) => (
                      <TableRow
                        key={lora.lora_id}
                        className="hover:bg-[#DFFE00]/5 border-white/10"
                      >
                        <TableCell className="font-medium text-white">
                          {lora.name}
                          <span className="text-xs text-white/60 ml-2">
                            v{lora.version}
                          </span>
                        </TableCell>
                        <TableCell className="text-white/80">
                          {lora.trigger_word}
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(lora.training_status)}
                        </TableCell>
                        <TableCell className="text-white/60 text-sm">
                          {formatDate(lora.created_at)}
                        </TableCell>
                        <TableCell className="text-white/60 text-sm">
                          {formatDate(lora.last_updated)}
                        </TableCell>
                        <TableCell>
                          <div className="flex space-x-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-white/60 hover:text-primary hover:bg-[#DFFE00]/10"
                              onClick={() => {
                                // Ensure training_images exists and is an array
                                const trainingImages = Array.isArray(
                                  lora.training_images,
                                )
                                  ? lora.training_images
                                  : [];
                                viewTrainingImages(
                                  lora.lora_id,
                                  trainingImages,
                                );
                              }}
                              title="View training images"
                            >
                              <Eye size={16} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-white/60 hover:text-red-500 hover:bg-red-500/10"
                              onClick={() => handleDeleteLora(lora.lora_id)}
                              title="Delete model"
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Image Preview Dialog */}
      <Dialog open={showImagePreview} onOpenChange={setShowImagePreview}>
        <DialogContent className="max-w-4xl bg-black/90 border-white/10 backdrop-blur-xl">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-primary">
              Training Images
            </DialogTitle>
            <DialogDescription className="text-white/60">
              {previewImages2.images.length} images used to train this custom
              model
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-4 max-h-[600px] overflow-y-auto p-1">
            {previewImages2.images.length > 0 ? (
              previewImages2.images.map((img, idx) => {
                // For GCS URLs, remove the 'gs://' prefix to make it work with get-file
                const processedUrl = img.startsWith("gs://")
                  ? img.substring(5)
                  : img;

                return (
                  <div
                    key={idx}
                    className="aspect-square border border-white/10 rounded-lg overflow-hidden hover:border-primary/50 transition-all duration-200"
                  >
                    <img
                      src={`${apiUrl}/get-file?url=${encodeURIComponent(processedUrl)}`}
                      alt={`Training image ${idx + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        // Fallback for failed images
                        e.currentTarget.src =
                          "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 24 24' fill='none' stroke='%23ffffff' stroke-width='1' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='3' y='3' width='18' height='18' rx='2' ry='2'%3E%3C/rect%3E%3Ccircle cx='9' cy='9' r='2'%3E%3C/circle%3E%3Cpath d='m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21'%3E%3C/path%3E%3C/svg%3E";
                        e.currentTarget.classList.add("p-4", "bg-black/40");
                      }}
                    />
                  </div>
                );
              })
            ) : (
              <div className="col-span-3 flex flex-col items-center justify-center py-12 text-white/60">
                <ImageIcon className="h-12 w-12 mb-2 text-white opacity-20" />
                <p>No training images available</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoraTrainingPage;
